import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useAppDispatch from '@/hooks/useAppDispatch'
import useAppSelector from '@/hooks/useAppSelector'
import { useIsPdf } from '@/hooks/useIsPdf'
import { THEME_PALETTE } from '@/lib/constants'
import { faCheck, faTimes } from '@/lib/fontAwesomePro/regular'
import { faArrowDown } from '@/lib/fontAwesomePro/solid'
import { cn } from '@/lib/utils'
import {
  selectIsSignable,
  selectSignature,
  setIsShowingSubmitModal,
  setSignature,
} from '@/redux/slices/referralSignatureConsentPdf'

const BOX_HEIGHT = 55

const SignatureBox: React.FC = () => {
  const signature = useAppSelector(selectSignature)
  const isSignable = useAppSelector(selectIsSignable)
  const dispatch = useAppDispatch()
  const [isSigning, setIsSigning] = useState(false)
  const [name, setName] = useState(signature)
  const isValidName = name.trim().length > 0
  const isPdf = useIsPdf()

  if (isSigning) {
    return (
      <div
        className="flex items-center"
        style={{ height: BOX_HEIGHT, width: '450px' }}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault()

            if (!isValidName) {
              return
            }

            dispatch(setSignature(name))
            setIsSigning(false)
            dispatch(setIsShowingSubmitModal(true))
          }}
          className="flex w-full items-stretch"
          style={{ height: '32px', gap: '2px' }}
        >
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            type="text"
            className="mr-1"
            placeholder="Your full name"
            style={{ maxWidth: '210px' }}
            autoFocus
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={() => {
                  if (isSignable) {
                    dispatch(setSignature(name))
                  }
                }}
                type="submit"
                variant="success"
                disabled={!isValidName}
              >
                <FontAwesomeIcon
                  icon={faCheck}
                  className="text-md text-white"
                />
              </Button>
            </TooltipTrigger>

            <TooltipContent>Save changes</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="destructive"
                onClick={() => {
                  setName('')
                  setIsSigning(false)
                }}
              >
                <FontAwesomeIcon
                  icon={faTimes}
                  className="text-md text-white"
                />
              </Button>
            </TooltipTrigger>

            <TooltipContent>Cancel changes</TooltipContent>
          </Tooltip>
        </form>
      </div>
    )
  }

  const shouldShowEditButton = isSignable && !isPdf

  if (signature) {
    return (
      <div
        className="flex items-center gap-4"
        style={{ minHeight: BOX_HEIGHT }}
      >
        <div
          className="font-bold text-black italic"
          style={{ fontSize: '34px', fontFamily: 'Allura', lineHeight: '1.4' }}
        >
          {signature}
        </div>

        {shouldShowEditButton && (
          <Button variant="plain" onClick={() => setIsSigning(true)}>
            (Edit)
          </Button>
        )}
      </div>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <section
          className={cn(
            'flex items-center justify-center rounded-md text-center text-black',
            isSignable ? 'hover-dim cursor-pointer' : '',
          )}
          style={{
            backgroundColor: THEME_PALETTE.yellowDocuSign,
            height: BOX_HEIGHT,
            width: '170px',
          }}
          onClick={() => {
            if (!isSignable) {
              return
            }

            setIsSigning(true)
          }}
        >
          <FontAwesomeIcon
            icon={faArrowDown}
            style={{ fontSize: '18px', marginRight: '7px' }}
          />{' '}
          SIGN
        </section>
      </TooltipTrigger>

      <TooltipContent>
        {isSignable ? 'Click to sign' : 'The client will be able to sign here'}
      </TooltipContent>
    </Tooltip>
  )
}

export default SignatureBox
